import { observePerformance } from '../observePerformance';
import { formatBytes } from './formatBytes';

type EndpointUrl = string;
type CallsDictionary = Record<EndpointUrl, PerformanceResourceTiming[]>;

export interface ObservedCall {
    url: string;
    count: number;
    isFile: boolean;
}

const removeLeadingSlash = (url: string): string => {
    if (!url.startsWith('/')) {
        return url;
    }

    return url.substring(1);
};

const parseUrlEndpoint = (url: string): string => {
    if (url.includes('https://alex-proxy.beeone.at/zuul')) {
        const { search } = new URL(url);
        const urlParams = new URLSearchParams(search);
        return parseUrlEndpoint(urlParams.get('redirectUrl') ?? '');
    }

    const { pathname, search } = new URL(url);
    if (pathname.includes('http') && pathname.startsWith('/')) {
        const parsedURL = new URL(pathname.substring(1));
        // url can contain appended urls due to proxy
        return parsedURL.pathname + search;
    }

    return pathname + search;
};

const MIN_DURATION_CACHE = 30;
const MULTIPLIER = 100;
// Timing-Allow-Origin CORS header needed for the transferSize
const withDetails = (logMessage: string, performanceEntry: PerformanceResourceTiming): string => {
    return `${logMessage}; duration ${
        Math.round(performanceEntry.duration * MULTIPLIER) / MULTIPLIER
    } ms; size: ${formatBytes(performanceEntry.transferSize)}`;
};

const isFromCache = ({
    transferSize,
    decodedBodySize,
    duration,
}: {
    transferSize: number;
    decodedBodySize: number;
    duration: number;
}): boolean => {
    if (transferSize > 0) {
        return false;
    }

    if (decodedBodySize > 0) {
        return true;
    }

    // fall back to duration checking (non-RT2 or cross-origin)
    return duration < MIN_DURATION_CACHE;
};

const consoleStyles = 'background: #21416c; color: white; padding: 3px; border-radius: 5px;';
const appendCacheLogIfCached = (logMessage: string, isCached: boolean) => {
    if (!isCached) {
        return logMessage;
    }

    return `${logMessage}; isCached: true`;
};

let callsDictionary: CallsDictionary = {};

const updateCallsDictionaryCount = (
    entryName: string,
    nextValueForEntryName: PerformanceResourceTiming[],
): void => {
    callsDictionary[entryName] = nextValueForEntryName;
};
const logToConsole = (
    performanceResource: PerformanceResourceTiming,
    urlName: string,
    callCount: number,
): void => {
    const messageTemplate = withDetails(
        appendCacheLogIfCached(`${urlName}; Count: ${callCount}`, isFromCache(performanceResource)),
        performanceResource,
    );
    // eslint-disable-next-line no-console,no-restricted-syntax
    console.log('%c[Requests counter]', consoleStyles, messageTemplate);
};

const buildFileExtensions = ['.json', '.woff2', '.js', '.svg', '.png', '.webmanifest', '.css'];
const isFile = (path: string): boolean => {
    if (path.includes('image/svg')) {
        return true;
    }
    return buildFileExtensions.some((fileExtension) => path.includes(fileExtension));
};

const isFileResource = (entry: PerformanceEntry): boolean =>
    (entry as PerformanceResourceTiming).initiatorType === 'script' || isFile(entry.name);

export const getObservedCalls = (onlyApi: boolean = false): ObservedCall[] =>
    Object.entries(callsDictionary)
        .sort(([, a], [, b]) => b.length - a.length)
        .map(([performanceResourceName, performanceTimingsArray]) => ({
            url: removeLeadingSlash(parseUrlEndpoint(performanceResourceName)),
            count: performanceTimingsArray.length,
            isFile: isFileResource(performanceTimingsArray[0]),
        }))
        .filter((call) => !onlyApi || !call.isFile);

export const getObservedCallCount = (onlyApi: boolean = false): number => {
    return Object.values(callsDictionary)
        .filter((calls) => !onlyApi || !isFileResource(calls[0]))
        .reduce((acc, current) => acc + current.length, 0);
};

/**
 * observe all API requests for dev tooling use only.
 * */
export const requestsObserverDevTool = (): ReturnType<typeof observePerformance> | undefined => {
    callsDictionary = {};
    // eslint-disable-next-line consistent-return
    return observePerformance(['resource'], (__, entry) => {
        const nextValueForEntryName = [
            ...(callsDictionary[entry.name] ?? []),
            entry as PerformanceResourceTiming,
        ];
        updateCallsDictionaryCount(entry.name, nextValueForEntryName);

        const urlName = removeLeadingSlash(parseUrlEndpoint(entry.name));
        const callCount = callsDictionary[entry.name].length;

        logToConsole(entry as PerformanceResourceTiming, urlName, callCount);
    });
};
